/* @flow */

import classNames from 'classnames';
import { isEmpty } from 'ramda';
import * as React from 'react';
import createUseThemeStyles from 'src/shared/app/base/util/createUseThemeStyles';
import {
  getUserAvatar,
  isUserOnline,
  getUserFullName,
} from '@braindate/domain/lib/user/util';

import styles from './UserAvatar.style';

import DefaultUserAvatar from './DefaultUserAvatar';
import UserOnlineBadge, {
  large as userOnlineBadgeLarge,
} from 'src/shared/components/domain/user/badge/UserOnlineBadge';
import UserBadge, {
  large as userBadgeLarge,
} from 'src/shared/components/domain/user/badge/UserBadge';
import Avatar from 'src/shared/ui/component/image/Avatar';
import DefaultAvatar from 'src/shared/ui/component/image/DefaultAvatar';
import {
  xsmall,
  small,
  medium,
  large,
  xlarge,
  xxlarge,
  mobilePeopleCardSize,
  peopleCardSize,
} from 'src/shared/ui/component/image/setting/avatarVariants';

import type { AvatarProps } from 'src/shared/ui/component/image/type/avatarTypes';
import type { User } from '@braindate/domain/lib/user/type';

type Props = AvatarProps & {
  user: User,
  showOnlineBadge?: boolean,
  isHost?: boolean,
  useDefaultAvatar?: boolean,
  onDarkBackground?: boolean,
  'aria-label'?: string,
};

const useStyles = createUseThemeStyles(styles);

const UserAvatar = ({
  layoutClass,
  variant,
  user,
  showOnlineBadge,
  isHost,
  onDarkBackground,
  useDefaultAvatar,
  'aria-label': ariaLabel,
}: Props): React.Node => {
  const avatar = getUserAvatar(user);

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes = useStyles({ isHost });

  const userOnlineBadgeClasses = classNames(classes.badge, {
    [classes['badge-small']]: variant === small,
    [classes['badge-xsmall']]: variant === xsmall,
    [classes['badge-medium']]: variant === medium,
    [classes['badge-large']]: variant === large,
    [classes['badge-xlarge']]: variant === xlarge,
    [classes['badge-xxlarge']]: variant === xxlarge,
    [classes['badge-mobilePeopleCardSize']]: variant === mobilePeopleCardSize,
    [classes['badge-peopleCardSize']]: variant === peopleCardSize,
  });

  const userBadgeClasses = classNames(classes.userBadge, {
    [classes['user-badge-small']]: variant === small,
    [classes['user-badge-xsmall']]: variant === xsmall,
    [classes['user-badge-medium']]: variant === medium,
    [classes['user-badge-large']]: variant === large,
    [classes['user-badge-xlarge']]: variant === xlarge,
    [classes['user-badge-xxlarge']]: variant === xxlarge,
    [classes['user-badge-mobilePeopleCardSize']]:
      variant === mobilePeopleCardSize,
    [classes['user-badge-peopleCardSize']]: variant === peopleCardSize,
  });

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  const avatarProps = {
    layoutClass,
    variant,
    alt: getUserFullName(user),
    'aria-label': ariaLabel,
  };

  let avatarElt = <DefaultUserAvatar {...avatarProps} user={user} />;

  if (avatar && !isEmpty(avatar)) {
    avatarElt = <Avatar {...avatarProps} src={avatar} />;
  } else if (useDefaultAvatar) {
    avatarElt = <DefaultAvatar {...avatarProps} />;
  }

  const userOnlineBadgeElt = showOnlineBadge && isUserOnline(user) && (
    <UserOnlineBadge
      layoutClass={userOnlineBadgeClasses}
      variant={
        [large, xlarge, xxlarge, mobilePeopleCardSize, peopleCardSize].includes(
          variant,
        )
          ? userOnlineBadgeLarge
          : null
      }
      onDarkBackground={onDarkBackground}
    />
  );

  const userBadgeElt = isHost && (
    <UserBadge
      layoutClass={userBadgeClasses}
      variant={
        [large, xlarge, xxlarge, mobilePeopleCardSize, peopleCardSize].includes(
          variant,
        )
          ? userBadgeLarge
          : null
      }
      onDarkBackground={onDarkBackground}
    />
  );

  return (
    <div className={classes.root}>
      {avatarElt}
      {userOnlineBadgeElt}
      {userBadgeElt}
    </div>
  );
};

// Make variants available
export * from 'src/shared/ui/component/image/setting/avatarVariants';

export default React.memo<Props>(UserAvatar);
